import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { Compass, Inbox, Loader2, Lock, TriangleAlert, WifiOff, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { isApiError } from '@/api/errors'
import { useApiErrorText } from '@/lib/error-text'
import { cn } from '@/lib/utils'
import { Button, buttonVariants } from './button'

function StateBlock({ icon: Icon, title, description, action, tone = 'neutral', role, className, children, page = false }: {
  icon: LucideIcon
  title: string
  description?: ReactNode
  action?: ReactNode
  tone?: 'neutral' | 'danger'
  role?: 'status' | 'alert'
  className?: string
  children?: ReactNode
  /** The block is the whole page content (404, 403): its title is the page's h1 */
  page?: boolean
}) {
  const Heading = page ? 'h1' : 'h2'
  return (
    <div role={role} className={cn('mx-auto flex max-w-md flex-col items-center gap-3 px-4 py-12 text-center', className)}>
      <Icon aria-hidden className={cn('size-8', tone === 'danger' ? 'text-danger' : 'text-fg-muted')} />
      <Heading className="text-h3 font-semibold">{title}</Heading>
      {description ? <p className="text-body text-fg-muted">{description}</p> : null}
      {children}
      {action ? <div className="mt-2 flex flex-wrap justify-center gap-3">{action}</div> : null}
    </div>
  )
}

export function Loading({ label, className }: { label?: string; className?: string }) {
  const { t } = useTranslation()
  return (
    <div role="status" className={cn('flex items-center justify-center gap-3 px-4 py-12 text-fg-muted', className)}>
      <Loader2 aria-hidden className="size-5 animate-spin motion-reduce:animate-none" />
      <span>{label ?? t('common.loading')}</span>
    </div>
  )
}

export function Empty({ title, description, action, icon = Inbox }: { title: string; description?: string; action?: ReactNode; icon?: LucideIcon }) {
  return <StateBlock icon={icon} title={title} {...(description ? { description } : {})} {...(action ? { action } : {})} />
}

export function ErrorState({ error, onRetry, title, page }: { error?: unknown; onRetry?: () => void; title?: string; page?: boolean }) {
  const { t } = useTranslation()
  const text = useApiErrorText()
  const requestId = isApiError(error) ? error.requestId : undefined
  return (
    <StateBlock
      {...(page ? { page } : {})}
      role="alert"
      tone="danger"
      icon={TriangleAlert}
      title={title ?? t('states.error.title')}
      description={error ? text(error) : t('states.error.description')}
      action={onRetry ? <Button variant="secondary" onClick={onRetry}>{t('common.retry')}</Button> : undefined}
    >
      {requestId ? (
        <p className="text-caption text-fg-muted">
          {t('states.error.reference')}: <span className="tabular">{requestId}</span>
        </p>
      ) : null}
    </StateBlock>
  )
}

export function Forbidden({ description }: { description?: string }) {
  const { t } = useTranslation()
  return <StateBlock page icon={Lock} title={t('states.forbidden.title')} description={description ?? t('states.forbidden.description')} />
}

export function Offline({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation()
  return (
    <StateBlock
      role="alert"
      icon={WifiOff}
      title={t('states.offline.title')}
      description={t('states.offline.description')}
      action={onRetry ? <Button variant="secondary" onClick={onRetry}>{t('common.retry')}</Button> : undefined}
    />
  )
}

export function NotFound({ homeTo = '/' }: { homeTo?: string }) {
  const { t } = useTranslation()
  return (
    <StateBlock
      page
      icon={Compass}
      title={t('states.notFound.title')}
      description={t('states.notFound.description')}
      action={<Link to={homeTo} className={buttonVariants({ variant: 'primary' })}>{t('states.notFound.action')}</Link>}
    />
  )
}

/** Picks the right page state for a failed query. */
export function QueryError({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (isApiError(error)) {
    if (error.isNetwork) return <Offline {...(onRetry ? { onRetry } : {})} />
    if (error.status === 403) return <Forbidden />
    if (error.status === 404) return <NotFound />
  }
  return <ErrorState error={error} {...(onRetry ? { onRetry } : {})} />
}
